// SOURCE: FRD §3.2, §3.3, §3.4 — Content Packs, Premium Frameworks, All Access.
// Pack→frameworks mapping intentionally left empty until product spec is delivered.
// Prices are USD cents to match existing pricing.ts convention.

export type PackType = "base" | "thematic" | "regional" | "premium_framework";

export interface ContentPack {
  slug: string;
  name: string;
  packType: PackType;
  /** Monthly price in cents. Annual = monthly × 12 × (1 - 0.20). */
  monthlyPriceCents: number;
  /** Whether this pack consumes a Foundation/Pro slot. */
  slotEligible: boolean;
  /** ISO-3166 region codes or jurisdiction tags shown as chips on the pack card. */
  jurisdictions: string[];
  /** Slug of the premium framework bundled at no extra cost (if any). */
  bundledPremiumFramework?: string;
  /** Frameworks included in this pack. EMPTY until product spec is provided. */
  frameworks: string[];
  /** Short description for pack cards. */
  description: string;
  /**
   * The regulations this pack's homepage tile names, and the published
   * template each one links to. Present only on the packs that have shipped to
   * the homepage grid — the ship rule is that every regulation named on a tile
   * is a published package with requirement content behind it.
   *
   * `label` is what the tile prints and is deliberately NOT translated: a
   * category word ("privacy regulations") destroys the whole claim, which is
   * specificity. `slug` is the template's identity, not its copy, so renaming
   * a template cannot silently break the link. `contentPacks.test.ts` asserts
   * every slug here is unique and well formed; the live check against
   * published templates is the SQL in that test's comment.
   */
  tileRegulations?: readonly { label: string; slug: string }[];
  /**
   * Present when this pack has an argument worth its own page at
   * /programs/<packUrlId>. Absent means the pack tile goes to the filtered
   * template library instead, which is the honest destination for a pack that
   * has content but no wedge.
   *
   * There is deliberately no per-pack layout here. Two hand-built wedge pages
   * (Aug 2026) drifted apart within a week and one of them phrased its product
   * boundary in the first person, which reads as cSquare declining privacy
   * duties it actually owes. One template, fixed chrome, copy as data.
   */
  program?: PackProgram;
}

export interface PackProgram {
  /** Copy lives at programs.<i18nKey>.* in the locale files. */
  i18nKey: string;
  /**
   * Published template slugs for this program, in display order. The page
   * computes every number from the live rows these resolve to, so a figure on
   * the page cannot drift from the platform.
   */
  slugs: readonly string[];
  /** Optional slug singled out below the proof grid as the sharpest example. */
  flagshipSlug?: string;
  /** Proof grid columns at the widest breakpoint. */
  proofColumns?: 3 | 4;
  /** SEO keywords, comma separated. Not translated. */
  seoKeywords: string;
}

export interface PremiumFramework {
  slug: string;
  name: string;
  monthlyPriceCents: number;
  /** Slug of the pack that includes this framework at no extra cost. */
  bundledInPack?: string;
  /** Short description shown under the framework name. */
  description: string;
}

export const ANNUAL_DISCOUNT = 0.2;

export const annualFromMonthlyCents = (monthlyCents: number): number =>
  Math.round(monthlyCents * 12 * (1 - ANNUAL_DISCOUNT));

// ---------- 10 Content Packs (FRD §3.2) ----------
export const CONTENT_PACKS: ContentPack[] = [
  {
    slug: "pack_financial_crime",
    name: "Financial Crime",
    packType: "thematic",
    monthlyPriceCents: 30000,
    slotEligible: true,
    jurisdictions: ["INTL", "US", "EU", "UK"],
    frameworks: [], // TODO: populate from product spec
    description:
      "AML, CFT, sanctions, and anti-bribery frameworks for financial institutions and fintechs.",
  },
  {
    slug: "pack_privacy_global",
    name: "Privacy Global",
    packType: "thematic",
    monthlyPriceCents: 25000,
    slotEligible: true,
    jurisdictions: ["EU", "US", "UK", "BR", "CA", "ZA", "SG", "JP"],
    frameworks: [],
    description:
      "GDPR, UK GDPR, CCPA/CPRA, LGPD, PIPEDA, POPIA, PDPA Singapore, APPI and global privacy standards.",
    tileRegulations: [
      { label: "GDPR", slug: "gdpr" },
      { label: "CCPA/CPRA", slug: "ccpa" },
      { label: "India DPDP", slug: "india-dpdp-act" },
    ],
    program: {
      i18nKey: "privacyGlobal",
      slugs: [
        "gdpr",
        "us-electronic-surveillance-privacy",
        "lgpd",
        "ccpa",
        "india-dpdp-act",
        "soc-2-privacy-addon",
        "popia",
        "privacy-by-design",
      ],
      flagshipSlug: "us-electronic-surveillance-privacy",
      proofColumns: 4,
      seoKeywords:
        "GDPR Article 5(2), GDPR Article 24, demonstrable accountability, privacy program evidence, CCPA, India DPDP, LGPD, POPIA, privacy compliance evidence",
    },
  },
  {
    slug: "pack_healthcare_plus",
    name: "Healthcare Plus",
    packType: "thematic",
    monthlyPriceCents: 30000,
    slotEligible: true,
    jurisdictions: ["US", "INTL"],
    frameworks: [],
    description:
      "HIPAA, HITECH, HITRUST and FDA 21 CFR Part 11 for regulated healthcare and life-sciences programs.",
    tileRegulations: [
      { label: "HIPAA", slug: "hipaa-compliance" },
      { label: "HITRUST", slug: "hitrust-csf" },
      { label: "FDA 21 CFR Part 11", slug: "fda-21-cfr-part-11" },
    ],
  },
  {
    slug: "pack_public_sector",
    name: "Public Sector / Defense",
    packType: "thematic",
    monthlyPriceCents: 40000,
    slotEligible: true,
    jurisdictions: ["US"],
    bundledPremiumFramework: "framework_fedramp_high",
    frameworks: [],
    description:
      "FedRAMP High, CMMC Level 3, NIST 800-171, StateRAMP, TX-RAMP for government and defense contractors.",
    tileRegulations: [
      { label: "FedRAMP", slug: "fedramp-authorization" },
      { label: "CMMC Level 2", slug: "cmmc-level-2" },
      { label: "NIST 800-53", slug: "nist-800-53" },
    ],
  },
  {
    slug: "pack_hr_workforce",
    name: "HR & Workforce",
    packType: "thematic",
    monthlyPriceCents: 20000,
    slotEligible: true,
    jurisdictions: ["INTL"],
    frameworks: [],
    description:
      "Workforce conduct, whistleblower, equal-opportunity, and HR data-handling controls.",
  },
  {
    slug: "pack_africa_fintech",
    name: "Africa Fintech & Finance",
    packType: "regional",
    monthlyPriceCents: 30000,
    slotEligible: true,
    jurisdictions: ["KE", "NG", "ZA"],
    frameworks: [],
    description:
      "Kenya DPA, Nigeria NDPA, POPIA, plus AML/CFT controls tailored for African fintechs and banks.",
  },
  {
    slug: "pack_mena_finance",
    name: "MENA Finance & Crypto",
    packType: "regional",
    monthlyPriceCents: 35000,
    slotEligible: true,
    jurisdictions: ["AE", "SA", "TR"],
    frameworks: [],
    description:
      "UAE PDPL, Saudi PDPL, Türkiye KVKK, plus regional AML/crypto-asset frameworks.",
  },
  {
    slug: "pack_eu_regional",
    name: "EU Regional",
    packType: "regional",
    monthlyPriceCents: 25000,
    slotEligible: true,
    jurisdictions: ["EU"],
    bundledPremiumFramework: "framework_dora",
    frameworks: [],
    description:
      "EU NIS2, EU AI Act, ePrivacy, Data Act, MiCA, PSD2/PSD3 — Europe-specific regulatory coverage.",
    tileRegulations: [
      { label: "DORA", slug: "dora" },
      { label: "NIS2", slug: "nis2-directive" },
      { label: "EU AI Act", slug: "eu-ai-act" },
    ],
  },
  {
    slug: "pack_apac_regional",
    name: "APAC Regional",
    packType: "regional",
    monthlyPriceCents: 25000,
    slotEligible: true,
    jurisdictions: ["SG", "AU", "HK", "JP", "KR", "IN"],
    frameworks: [],
    description:
      "MAS TRM, APRA CPS 230/234, HKMA Operational Resilience, India DPDP, K-ISMS and APAC standards.",
  },
  {
    slug: "pack_americas_regional",
    name: "Americas Regional",
    packType: "regional",
    monthlyPriceCents: 20000,
    slotEligible: true,
    jurisdictions: ["US", "CA", "MX", "BR", "AR"],
    frameworks: [],
    description:
      "State-level US privacy laws (VCDPA, CPA), PIPEDA, LFPDPPP, LGPD, Argentina PDPL.",
    tileRegulations: [
      { label: "SOC 2", slug: "soc-2-type-ii" },
      { label: "ISO 27001", slug: "iso-27001-isms" },
      { label: "PCI DSS", slug: "pci-dss" },
    ],
  },
  {
    slug: "pack_federal_grants",
    name: "Federal Grants (US)",
    packType: "thematic",
    monthlyPriceCents: 30000,
    slotEligible: true,
    jurisdictions: ["US"],
    frameworks: [],
    description:
      "Single Audit (2 CFR §200.501), subrecipient monitoring (§200.331–.333), WIOA / 29 CFR §38.28 equal opportunity, and FFATA/FSRS subaward reporting for state & local governments, nonprofits, and WIOA subrecipients.",
    tileRegulations: [
      { label: "2 CFR §200.501 Single Audit", slug: "uniform-guidance" },
      { label: "WIOA §38.28", slug: "wioa" },
      { label: "Federal Grant Management Standards", slug: "fgms" },
    ],
    program: {
      i18nKey: "federalGrants",
      slugs: ["uniform-guidance", "wioa", "fgms"],
      proofColumns: 3,
      seoKeywords:
        "2 CFR 200, Uniform Guidance, Single Audit, subrecipient monitoring, WIOA 38.28, federal grant compliance, federal award compliance, grant management standards",
    },
  },

];

// ---------- 5 Premium Standalone Frameworks (FRD §3.3) ----------
export const PREMIUM_FRAMEWORKS: PremiumFramework[] = [
  {
    slug: "framework_fedramp_high",
    name: "FedRAMP High",
    monthlyPriceCents: 80000,
    bundledInPack: "pack_public_sector",
    description:
      "U.S. federal cloud authorization at the High impact level — required for sensitive federal workloads.",
  },
  {
    slug: "framework_dora",
    name: "EU DORA",
    monthlyPriceCents: 60000,
    bundledInPack: "pack_eu_regional",
    description:
      "Digital Operational Resilience Act for EU financial entities and their critical ICT third parties.",
  },
  {
    slug: "framework_basel",
    name: "Basel III / IV",
    monthlyPriceCents: 70000,
    description:
      "International capital, liquidity, and risk standards for banking organizations.",
  },
  {
    slug: "framework_cmmc_l3",
    name: "CMMC Level 3",
    monthlyPriceCents: 70000,
    bundledInPack: "pack_public_sector",
    description:
      "Cybersecurity Maturity Model Certification Level 3 for U.S. defense supply-chain contractors.",
  },
  {
    slug: "framework_mica",
    name: "MiCA",
    monthlyPriceCents: 50000,
    bundledInPack: "pack_eu_regional",
    description:
      "Markets in Crypto-Assets Regulation — EU framework for crypto-asset issuers and service providers.",
  },
];

// ---------- All Access bundle (FRD §3.4) ----------
export const ALL_ACCESS_MONTHLY_CENTS = 250000; // $2,500
export const ALL_ACCESS_ANNUAL_CENTS = 2400000; // $24,000

// ---------- Core 10 (placeholder list — confirm with product before launch) ----------
// TODO: confirm Core 10 list with product
export const CORE_10_FRAMEWORKS: string[] = [
  "SOC 2",
  "ISO 27001",
  "GDPR",
  "NIST CSF",
  "HIPAA",
  "PCI DSS 4.0",
  "CCPA / CPRA",
  "ISO 27701",
  "NIST 800-171",
  "CIS Controls v8",
];

// ---------- Helpers ----------
export const getPackBySlug = (slug: string): ContentPack | undefined =>
  CONTENT_PACKS.find((p) => p.slug === slug);

export const getPremiumBySlug = (slug: string): PremiumFramework | undefined =>
  PREMIUM_FRAMEWORKS.find((f) => f.slug === slug);

/** Sum of every pack monthly price (in cents). */
export const totalPacksMonthlyCents = (): number =>
  CONTENT_PACKS.reduce((sum, p) => sum + p.monthlyPriceCents, 0);

/** Sum of every premium framework monthly price (in cents). */
export const totalPremiumMonthlyCents = (): number =>
  PREMIUM_FRAMEWORKS.reduce((sum, f) => sum + f.monthlyPriceCents, 0);

/** All Access monthly savings vs à-la-carte (in cents). */
export const allAccessMonthlySavingsCents = (): number =>
  totalPacksMonthlyCents() + totalPremiumMonthlyCents() - ALL_ACCESS_MONTHLY_CENTS;

/** All Access annual savings vs à-la-carte (in cents). */
export const allAccessAnnualSavingsCents = (): number =>
  allAccessMonthlySavingsCents() * 12;

/** URL id used by the homepage tiles and `/templates?pack=`. */
export const packUrlId = (pack: ContentPack): string =>
  pack.slug.replace(/^pack_/, "").replace(/_/g, "-");

export const getPackByUrlId = (id: string | null | undefined): ContentPack | undefined =>
  id ? CONTENT_PACKS.find((p) => packUrlId(p) === id) : undefined;

/** The packs that have shipped to the homepage grid, in grid order. */
/** Packs that have an argument page at /programs/<packUrlId>. */
export const packsWithPrograms = (): ContentPack[] =>
  CONTENT_PACKS.filter((p) => p.program !== undefined);

/** Resolve a /programs/:pack URL segment to its pack, or undefined. */
export const getProgramPackByUrlId = (id: string | undefined): ContentPack | undefined => {
  const pack = getPackByUrlId(id);
  return pack?.program ? pack : undefined;
};

export const shippedHomepagePacks = (): ContentPack[] =>
  CONTENT_PACKS.filter((p) => p.tileRegulations && p.tileRegulations.length > 0);

/**
 * Narrow a list of templates to the regulations a pack names.
 *
 * Pure, and matched on slug rather than display name or category, because a
 * pack cuts across the library's categories: EU Regional's three regulations
 * sit in three different ones, and Federal Grants and Public Sector would both
 * collapse to "government".
 *
 * Returns an empty array when the pack is unknown or none of its templates are
 * published. Callers treat empty as "no pack filter" and show the full library
 * rather than an empty shelf.
 */
export const templatesInPack = <T extends { dbSlug?: string }>(
  templates: readonly T[],
  packId: string | null | undefined,
): T[] => {
  const pack = getPackByUrlId(packId);
  if (!pack?.tileRegulations?.length) return [];
  const wanted = new Set(pack.tileRegulations.map((r) => r.slug));
  return templates.filter((t) => t.dbSlug !== undefined && wanted.has(t.dbSlug));
};
